/**
 * Compute Capability Service — wraps /api/admin/platform/compute-capability/
 * Phase 4.8-C — WQ-201
 */

export type ComputeTierValue =
  | 'CPU_NATIVE'
  | 'CPU_VIABLE'
  | 'CPU_INFERENCE_GPU_TRAINING'
  | 'GPU_STRONGLY_PREFERRED'
  | 'GPU_REQUIRED';

export interface ComputeCapability {
  id: string;
  slug: string;
  gpu_available: boolean;
  gpu_count: number;
  gpu_model: string | null;
  gpu_vram_mb_per_device: number | null;
  cuda_version: string | null;
  cpu_count: number;
  cpu_model: string | null;
  total_ram_mb: number;
  available_ram_mb: number;
  intel_amx_available: boolean;
  onednn_bf16_enabled: boolean;
  max_supported_tier: ComputeTierValue;
  detection_method: string;
  last_detected_at: string;
}

export interface CompatibilityResult {
  algorithm_id: string;
  algorithm_slug: string;
  compute_tier: ComputeTierValue;
  compatible: boolean;
  warning_level: 'none' | 'info' | 'warning' | 'hard_warning' | 'blocked';
  warning_message: string;
  estimated_seconds: number | null;
  recommended_action: string | null;
}

type FetchFn = (url: string, options?: RequestInit) => Promise<Response>;

const BASE = '/api/admin/platform/compute-capability';

export async function getComputeCapability(
  authFetch: FetchFn,
  useCache = true,
): Promise<ComputeCapability> {
  const qs = useCache ? '' : '?use_cache=false';
  const res = await authFetch(`${BASE}/${qs}`);
  if (!res.ok) {
    throw new Error(`Failed to fetch compute capability: ${res.status}`);
  }
  return res.json();
}

export async function refreshComputeCapability(
  authFetch: FetchFn,
): Promise<ComputeCapability> {
  const res = await authFetch(`${BASE}/refresh`, { method: 'POST' });
  if (!res.ok) {
    throw new Error(`Failed to refresh compute capability: ${res.status}`);
  }
  return res.json();
}

export async function getAlgorithmCompatibility(
  authFetch: FetchFn,
  algorithmId: string,
  datasetRows?: number,
): Promise<CompatibilityResult> {
  const qs = datasetRows ? `?dataset_rows=${datasetRows}` : '';
  const res = await authFetch(
    `${BASE}/algorithm/${algorithmId}/compatibility${qs}`,
  );
  if (!res.ok) {
    throw new Error(`Failed to evaluate compatibility: ${res.status}`);
  }
  return res.json();
}
